import { PRIVILEGES } from 'constants/privileges';
import { AccountEntry } from 'api/accountEntries/accountEntries.types';
import { Activity } from 'api/activities/activities.types';
import { ExchangeRateValue } from 'api/invoices/invoices.types';
import _ from 'lodash';
import {
  CorrectiveInvoice,
  Invoice,
  InvoiceLine,
  InvoiceLineAmounts,
  InvoiceTotals,
  InvoiceClass,
} from 'pages/invoices/invoices.types';
import { InvoiceTab } from 'root/pathnames';
import { User } from 'types/user';
import {
  hasPrivilege,
  isLoggedUserOrReplacement,
  isLoggedUserOrReplacementByCode,
} from './authFunctions.utils';
import { toFixedCurrency } from './numberFunctions.utils';

export const isInvoiceAdministrative = (invoiceClass: InvoiceClass) =>
  invoiceClass?.type.toLowerCase().includes('administrative');

export const isInvoiceCorrective = (invoiceClass: InvoiceClass) =>
  invoiceClass?.type.toLowerCase().includes('corrective');

export const isForeignInvoice = (invoiceClass: InvoiceClass) =>
  !invoiceClass.own;

export const isInvoiceInternal = (invoiceClass: InvoiceClass) =>
  !invoiceClass.own && invoiceClass?.type === 'INTERNAL_CHARGE';

export const isKsefInvoiceClass = (invoiceClass: InvoiceClass) =>
  invoiceClass.own && ['FVS', 'FKS', 'FNPS', 'FNPKS'].includes(invoiceClass.code);

// copied from original repository - storm/nautiqus
// nautiqus\frontend\src\main\webapp\frontend\src\modules\invoicesModule\invoices\invoice\invoiceBody.js
// then slightly refactored
export const canInvoiceBeUnpublished = (
  invoice: Invoice | CorrectiveInvoice,
  status: string,
  user: User | null,
  allowedReplacementsByCode: string[],
) => {
  if (status === 'EXPORTED') {
    return false;
  }
  if (invoice.deleted || !invoice.publicationDate) {
    return false;
  }
  if (isInvoiceAdministrative(invoice.invoiceClass)) {
    return hasPrivilege(PRIVILEGES.Invoices.PUBLISH_ADMIN_INVOICE, user);
  }

  if (isForeignInvoice(invoice.invoiceClass)) {
    return (
      hasPrivilege(PRIVILEGES.Invoices.PUBLISH_FOREIGN_INVOICE, user) ||
      isLoggedUserOrReplacementByCode(
        invoice?.owner?.code,
        user,
        allowedReplacementsByCode,
      )
    );
  }
  return false;
};

// copied from original repository - storm/nautiqus
export const canInvoiceBePublished = (
  invoice: Invoice | CorrectiveInvoice,
): boolean => {
  if (!isInvoiceCorrective(invoice.invoiceClass)) {
    return !invoice.publicationDate && invoice.invoiceLines.length > 0;
  }
  return (
    !invoice.publicationDate &&
    (invoice as CorrectiveInvoice).correctedInvoiceLines.length > 0 &&
    (invoice as CorrectiveInvoice).previousInvoiceLines.length > 0
  );
};

// copied from original repository - storm/nautiqus
export const canPublishInvoice = (
  invoice: Invoice | CorrectiveInvoice,
  user: User | null,
  allowedReplacementsByCode: string[],
): boolean => {
  if (
    invoice.deleted ||
    !!invoice.publicationDate ||
    invoice.ksefState === 'SENDING' ||
    invoice.ksefState === 'ACCEPTED'
  ) {
    return false;
  }

  const isOwner = isLoggedUserOrReplacementByCode(
    invoice?.owner?.code,
    user,
    allowedReplacementsByCode,
  );

  if (isInvoiceAdministrative(invoice.invoiceClass)) {
    return (
      hasPrivilege(PRIVILEGES.Invoices.PUBLISH_ADMIN_INVOICE, user) ||
      (hasPrivilege(PRIVILEGES.Invoices.PUBLISH_ADMIN_INVOICE_OWNER, user) &&
        isOwner)
    );
  }

  if (isForeignInvoice(invoice.invoiceClass)) {
    return hasPrivilege(PRIVILEGES.Invoices.PUBLISH_FOREIGN_INVOICE, user);
  }

  return (
    hasPrivilege(PRIVILEGES.Invoices.PUBLISH_OWN_INVOICE, user) ||
    (hasPrivilege(PRIVILEGES.Invoices.PUBLISH_OWN_INVOICE_OWNER, user) &&
      isOwner)
  );
};

// copied from original repository - storm/nautiqus
export const canChangeInvoiceStatus = (
  invoice: Invoice | CorrectiveInvoice,
  user: User | null,
  allowedReplacementsByCode: string[],
) => {
  if (invoice.deleted) {
    return false;
  }

  const isOwner = isLoggedUserOrReplacementByCode(
    invoice?.owner?.code,
    user,
    allowedReplacementsByCode,
  );

  if (isInvoiceAdministrative(invoice.invoiceClass)) {
    return (
      hasPrivilege(PRIVILEGES.Invoices.SETTLE_ADMIN_INVOICE, user) ||
      (hasPrivilege(PRIVILEGES.Invoices.SETTLE_ADMIN_INVOICE_OWNER, user) &&
        isOwner)
    );
  }

  if (isForeignInvoice(invoice.invoiceClass)) {
    return hasPrivilege(PRIVILEGES.Invoices.SETTLE_FOREIGN_INVOICE, user);
  }

  return (
    hasPrivilege(PRIVILEGES.Invoices.SETTLE_OWN_INVOICE, user) ||
    (hasPrivilege(PRIVILEGES.Invoices.SETTLE_OWN_INVOICE_OWNER, user) &&
      isOwner)
  );
};

// copied from original repository - storm/nautiqus
export const canPrintInvoice = (user: User | null) =>
  hasPrivilege(PRIVILEGES.Invoices.PRINT_INVOICE, user);

export const isReverted = (invoice: Invoice | CorrectiveInvoice) => {
  if (invoice.invoiceClass.own === false) {
    return !invoice.publicationDate && invoice.totals.netValueAllocationSum > 0;
  } else {
    return false;
  }
};

export const getExchangeRatesPayload = (
  invoice: Invoice | CorrectiveInvoice,
) => {
  if (
    invoice.currencyCode &&
    invoice.currencyCode.currency &&
    invoice.exchangeRateDate
  ) {
    if (!invoice.invoiceLines.length) return;

    const fromCurrencies = invoice.invoiceLines.reduce(
      (all: string[], invoiceLine) => {
        const currency = invoiceLine.currencyAmount.currencyCode.currency;
        if (currency) {
          return [...all, currency];
        }
        return all;
      },
      [],
    );

    if (invoice.issueDate) {
      return {
        from: [...Array.from(new Set(fromCurrencies))],
        to: invoice.currencyCode.currency,
        date: invoice.exchangeRateDate,
      };
    }
  }
};

export const getCorrectedExchangeRatesPayload = (
  invoice: CorrectiveInvoice,
) => {
  if (
    invoice.currencyCode &&
    invoice.currencyCode.currency &&
    invoice.exchangeRateDate
  ) {
    if (!invoice.correctedInvoiceLines.length) return;

    const fromCurrencies = invoice.correctedInvoiceLines.reduce(
      (all: string[], invoiceLine) => {
        const currency = invoiceLine.currencyAmount.currencyCode.currency;
        if (currency) {
          return [...all, currency];
        }
        return all;
      },
      [],
    );

    if (invoice.issueDate) {
      return {
        from: [...Array.from(new Set(fromCurrencies))],
        to: invoice.currencyCode.currency,
        date: invoice.exchangeRateDate,
      };
    }
  }
};

// recalculate invoice data when changing currency
// copied from original repository - storm/nautiqus
export const recalculateAllExchangeRates = (
  invoice: Invoice | CorrectiveInvoice,
  exchangeRates: ExchangeRateValue[],
) => {
  invoice.invoiceLines.forEach((invoiceLine) => {
    const currency = invoiceLine.currencyAmount.currencyCode.currency;
    const exchangeRate = exchangeRates.find(
      (e) => e.sourceCurrency === currency,
    );

    if (exchangeRate !== undefined) {
      invoiceLine.exchangeRate = exchangeRate.rate;
    }

    const priceWithExchangeRate = Number(
      toFixedCurrency(
        invoiceLine.currencyAmount.value * invoiceLine.exchangeRate,
        6,
      ),
    );
    invoiceLine.netUnitPriceInInvoiceCurrency = priceWithExchangeRate;

    const netValue = invoiceLine.quantity * priceWithExchangeRate;
    const propNetValue = toFixedCurrency(netValue, 2);
    invoiceLine.amounts.netValue = Number(propNetValue);

    const taxValue =
      invoiceLine.amounts.netValue * (invoiceLine.amounts.taxRate.value / 100);
    const propTaxValue = toFixedCurrency(taxValue, 2);
    invoiceLine.amounts.taxValue = Number(propTaxValue);

    const grossValue =
      invoiceLine.amounts.netValue + invoiceLine.amounts.taxValue;
    const propGrossValue = toFixedCurrency(grossValue, 2);
    invoiceLine.amounts.grossValue = Number(propGrossValue);
  });

  const taxTotalsUpdated = Object.values(
    _.groupBy(invoice.invoiceLines || [], (line) => line.amounts.taxRate.id),
  )
    .map((taxGroup) => ({
      amounts: calculateNetValue(taxGroup),
    }))
    .map((t) => calculateTaxAndGrossValue(t));
  invoice.taxTotals = taxTotalsUpdated;

  const totalsUpdated = calculateInvoiceTotals(invoice);
  invoice.totals = totalsUpdated;

  const { totals, taxTotals, invoiceLines } = invoice;

  return { totals, taxTotals, invoiceLines };
};

export const recalculateAllCorrectedExchangeRates = (
  invoice: CorrectiveInvoice,
  exchangeRates: ExchangeRateValue[],
) => {
  invoice.correctedInvoiceLines.forEach((invoiceLine) => {
    const currency = invoiceLine.currencyAmount.currencyCode.currency;
    const exchangeRate = exchangeRates.find(
      (e) => e.sourceCurrency === currency,
    );

    if (exchangeRate !== undefined) {
      invoiceLine.exchangeRate = exchangeRate.rate;
    }

    const priceWithExchangeRate = Number(
      toFixedCurrency(
        invoiceLine.currencyAmount.value * invoiceLine.exchangeRate,
        6,
      ),
    );
    invoiceLine.netUnitPriceInInvoiceCurrency = priceWithExchangeRate;

    const netValue = invoiceLine.quantity * priceWithExchangeRate;
    const propNetValue = toFixedCurrency(netValue, 2);
    invoiceLine.amounts.netValue = Number(propNetValue);

    const taxValue =
      invoiceLine.amounts.netValue * (invoiceLine.amounts.taxRate.value / 100);
    const propTaxValue = toFixedCurrency(taxValue, 2);
    invoiceLine.amounts.taxValue = Number(propTaxValue);

    const grossValue =
      invoiceLine.amounts.netValue + invoiceLine.amounts.taxValue;
    const propGrossValue = toFixedCurrency(grossValue, 2);
    invoiceLine.amounts.grossValue = Number(propGrossValue);
  });

  const { correctedInvoiceLines } = invoice;

  return { correctedInvoiceLines };
};

// recalculate invoice data when updating invoice lines
// copied from original repository - storm/nautiqus
export const recalculateTotals = (invoice: Invoice | CorrectiveInvoice) => {
  const taxTotalsUpdated = Object.values(
    _.groupBy(invoice.invoiceLines || [], (line) => line.amounts.taxRate.id),
  )
    .map((taxGroup) => ({
      amounts: calculateNetValue(taxGroup),
    }))
    .map((t) => calculateTaxAndGrossValue(t));
  invoice.taxTotals = taxTotalsUpdated;

  const totalsUpdated = calculateInvoiceTotals(invoice);
  invoice.totals = totalsUpdated;

  return invoice;
};

// recalculate corrective invoice data when updating previous invoice lines
// copied from original repository - storm/nautiqus
export const recalculatePreviousTotals = (invoice: CorrectiveInvoice) => {
  const previousTaxTotalsUpdated = Object.values(
    _.groupBy(
      invoice.previousInvoiceLines || [],
      (line) => line.amounts.taxRate.id,
    ),
  )
    .map((taxGroup) => ({
      amounts: calculateNetValue(taxGroup),
    }))
    .map((t) => calculateTaxAndGrossValue(t));
  invoice.previousTaxTotals = previousTaxTotalsUpdated;

  const previousTotalsUpdated = calculatePreviousInvoiceTotals(invoice);
  invoice.previousTotals = previousTotalsUpdated;

  const totalsUpdated = calculateCorrectiveInvoiceTotals(invoice);
  invoice.totals = { ...invoice.totals, ...totalsUpdated };

  return invoice;
};

// recalculate corrective invoice data when updating corrected invoice lines
// copied from original repository - storm/nautiqus
export const recalculateCorrectedTotals = (invoice: CorrectiveInvoice) => {
  const correctedTaxTotalsUpdated = Object.values(
    _.groupBy(
      invoice.correctedInvoiceLines || [],
      (line) => line.amounts.taxRate.id,
    ),
  )
    .map((taxGroup) => ({
      amounts: calculateNetValue(taxGroup),
    }))
    .map((t) => calculateTaxAndGrossValue(t));
  invoice.correctedTaxTotals = correctedTaxTotalsUpdated;

  const correctedTotalsUpdated = calculateCorrectedInvoiceTotals(invoice);
  invoice.correctedTotals = correctedTotalsUpdated;

  const totalsUpdated = calculateCorrectiveInvoiceTotals(invoice);
  invoice.totals = { ...invoice.totals, ...totalsUpdated };

  return invoice;
};

const calculateNetValue = (totals?: InvoiceLine[]) =>
  (totals || []).reduce(
    (amounts: InvoiceTotals & { taxRate: any }, invoiceLine) => {
      amounts.netValue =
        Number(toFixedCurrency(amounts.netValue, 2)) +
        Number(toFixedCurrency(invoiceLine.amounts.netValue, 2));
      amounts.taxRate = invoiceLine.amounts.taxRate;
      return amounts;
    },
    {
      netValueAllocationSum: 0,
      netValue: 0,
      taxValue: 0,
      grossValue: 0,
      taxRate: null,
    },
  );

const calculateTaxAndGrossValue = (taxTotal: {
  amounts: InvoiceLineAmounts;
}) => {
  taxTotal.amounts.taxValue = Number(
    toFixedCurrency(
      taxTotal.amounts.netValue * (taxTotal.amounts.taxRate.value / 100),
      2,
    ),
  );
  taxTotal.amounts.grossValue =
    taxTotal.amounts.netValue + taxTotal.amounts.taxValue;

  return taxTotal;
};

// calculate 'totals' field of invoice
// copied from original repository - storm/nautiqus
const calculateInvoiceTotals = (invoice: Invoice | CorrectiveInvoice) =>
  (invoice.taxTotals || []).reduce(
    (amounts: InvoiceTotals, invoiceSummary) => {
      amounts.netValue =
        Number(toFixedCurrency(amounts.netValue, 2)) +
        Number(toFixedCurrency(invoiceSummary.amounts!.netValue, 2));
      amounts.taxValue =
        Number(toFixedCurrency(amounts.taxValue, 2)) +
        Number(toFixedCurrency(invoiceSummary.amounts!.taxValue, 2));
      amounts.grossValue =
        Number(toFixedCurrency(amounts.grossValue, 2)) +
        Number(toFixedCurrency(invoiceSummary.amounts!.grossValue, 2));
      return amounts;
    },
    {
      netValueAllocationSum: invoice.totals.netValueAllocationSum || 0,
      netValue: 0,
      taxValue: 0,
      grossValue: 0,
    },
  );

// calculate 'previousTotals' field of corrective invoice
// copied from original repository - storm/nautiqus
const calculatePreviousInvoiceTotals = (invoice: CorrectiveInvoice) =>
  (invoice.previousTaxTotals || []).reduce(
    (amounts: InvoiceTotals, invoiceSummary) => {
      amounts.netValue =
        Number(toFixedCurrency(amounts.netValue, 2)) +
        Number(toFixedCurrency(invoiceSummary.amounts!.netValue, 2));
      amounts.taxValue =
        Number(toFixedCurrency(amounts.taxValue, 2)) +
        Number(toFixedCurrency(invoiceSummary.amounts!.taxValue, 2));
      amounts.grossValue =
        Number(toFixedCurrency(amounts.grossValue, 2)) +
        Number(toFixedCurrency(invoiceSummary.amounts!.grossValue, 2));
      return amounts;
    },
    { netValueAllocationSum: 0, netValue: 0, taxValue: 0, grossValue: 0 },
  );

// calculate 'correctedTotals' field of corrective invoice
// copied from original repository - storm/nautiqus
const calculateCorrectedInvoiceTotals = (invoice: CorrectiveInvoice) =>
  (invoice.correctedTaxTotals || []).reduce(
    (amounts: InvoiceTotals, invoiceSummary) => {
      amounts.netValue =
        Number(toFixedCurrency(amounts.netValue, 2)) +
        Number(toFixedCurrency(invoiceSummary.amounts!.netValue, 2));
      amounts.taxValue =
        Number(toFixedCurrency(amounts.taxValue, 2)) +
        Number(toFixedCurrency(invoiceSummary.amounts!.taxValue, 2));
      amounts.grossValue =
        Number(toFixedCurrency(amounts.grossValue, 2)) +
        Number(toFixedCurrency(invoiceSummary.amounts!.grossValue, 2));
      return amounts;
    },
    { netValueAllocationSum: 0, netValue: 0, taxValue: 0, grossValue: 0 },
  );

// calculate 'totals' field of corrective invoice
const calculateCorrectiveInvoiceTotals = (invoice: CorrectiveInvoice) => ({
  netValue: invoice.correctedTotals.netValue - invoice.previousTotals.netValue,
  taxValue: invoice.correctedTotals.taxValue - invoice.previousTotals.taxValue,
  grossValue:
    invoice.correctedTotals.grossValue - invoice.previousTotals.grossValue,
});

export const hasUnknownActivity = (
  invoice: Invoice | CorrectiveInvoice,
  activity: Activity,
) => {
  if (invoice.deleted) {
    return false;
  }

  return !invoice.invoiceClass.own && activity.discriminant === 'UNRECOGNIZED';
};

// copied from original repository - storm/nautiqus
const isOwnInvoiceDeleteEnabled = (
  selectedInvoices: Invoice[],
  user: User | null,
  allowedReplacements: number[],
) => {
  if (selectedInvoices.length > 0) {
    const undeletableInvoices = selectedInvoices.filter((invoice) => {
      if (!invoice || invoice.publicationDate) {
        return true;
      }
      if (
        hasPrivilege(PRIVILEGES.Invoices.DELETE_OWN_INVOICE_UNPUBLISHED, user)
      ) {
        return false;
      }

      return !(
        hasPrivilege(PRIVILEGES.Invoices.DELETE_OWN_INVOICE_OWNER, user) &&
        isLoggedUserOrReplacement(invoice?.owner?.id, user, allowedReplacements)
      );
    });

    return undeletableInvoices.length === 0;
  }
  return false;
};

// copied from original repository - storm/nautiqus
const isForeignInvoiceDeleteEnabled = (
  selectedInvoices: Invoice[],
  user: User | null,
) => {
  if (selectedInvoices.length > 0) {
    const undeletableInvoices = selectedInvoices.filter((invoice) => {
      if (!invoice || !!invoice.exportInformation || invoice.deleted === true) {
        return true;
      }

      if (invoice.publicationDate) {
        return true;
      }
      // TODO: PRIVILEGES
      return !hasPrivilege(
        PRIVILEGES.Invoices.DELETE_FOREIGN_UNPUBLISHED,
        user,
      );
    });
    return undeletableInvoices.length === 0;
  }
  return false;
};

// copied from original repository - storm/nautiqus
const isAdministrativeInvoiceDeleteEnabled = (
  selectedInvoices: Invoice[],
  user: User | null,
  allowedReplacements: number[],
) => {
  if (selectedInvoices.length > 0) {
    const undeletableInvoices = selectedInvoices.filter((invoice) => {
      if (!invoice || invoice.publicationDate) {
        return true;
      }

      if (
        hasPrivilege(PRIVILEGES.Invoices.DELETE_ADMIN_INVOICE_UNPUBLISHED, user)
      ) {
        return false;
      }
      return !(
        hasPrivilege(PRIVILEGES.Invoices.DELETE_OWN_INVOICE_OWNER, user) &&
        isLoggedUserOrReplacement(invoice?.owner?.id, user, allowedReplacements)
      );
    });
    return undeletableInvoices.length === 0;
  }
  return false;
};

export const isDeleteEnabled = {
  foreign: isForeignInvoiceDeleteEnabled,
  administrative: isAdministrativeInvoiceDeleteEnabled,
  own: isOwnInvoiceDeleteEnabled,
  internal: isForeignInvoiceDeleteEnabled, //TODO: Eventually change to specific privilege for INTERNAL
};

// copied from original repository - storm/nautiqus
export const invoiceHasLines = (invoice: Invoice | CorrectiveInvoice) => {
  if (isInvoiceCorrective(invoice.invoiceClass)) {
    return (
      (invoice as CorrectiveInvoice).previousInvoiceLines.length > 0 ||
      (invoice as CorrectiveInvoice).correctedInvoiceLines.length > 0
    );
  } else {
    return invoice.invoiceLines.length > 0;
  }
};

// copied from original repository - storm/nautiqus
// frontend\src\main\webapp\frontend\src\modules\invoicesModule\invoices\invoiceCommon.js
// renamed canWrite -> canEditInvoice
export const canEditInvoice = (
  invoice: Invoice | CorrectiveInvoice,
  user: User | null,
  allowedReplacementsByCode: string[],
) => {
  if (
    invoice.deleted ||
    invoice.ksefState === 'SENDING' ||
    invoice.ksefState === 'ACCEPTED'
  ) {
    return false;
  }

  const isOwner = isLoggedUserOrReplacementByCode(
    invoice?.owner?.code,
    user,
    allowedReplacementsByCode,
  );

  if (isInvoiceAdministrative(invoice.invoiceClass)) {
    return (
      hasPrivilege(PRIVILEGES.Invoices.EDIT_ADMIN_INVOICE, user) ||
      (hasPrivilege(PRIVILEGES.Invoices.EDIT_ADMIN_INVOICE_OWNER, user) &&
        isOwner)
    );
  }

  if (isForeignInvoice(invoice.invoiceClass)) {
    return hasPrivilege(PRIVILEGES.Invoices.EDIT_FOREIGN_INVOICE, user);
  }

  return (
    hasPrivilege(PRIVILEGES.Invoices.EDIT_OWN_INVOICE, user) ||
    (hasPrivilege(PRIVILEGES.Invoices.EDIT_OWN_INVOICE_OWNER, user) && isOwner)
  );
};

// copied from original repository - storm/nautiqus
// frontend\src\main\webapp\frontend\src\modules\invoicesModule\invoices\allocation.js
// renamed isExported -> isInvoiceAllocationExported
export const isInvoiceAllocationExported = (
  invoice: Invoice | CorrectiveInvoice,
) => {
  if (isForeignInvoice(invoice.invoiceClass)) {
    return invoice.exportInformation !== null;
  } else {
    return false;
  }
};

// copied from original repository - storm/nautiqus
// frontend\src\main\webapp\frontend\src\modules\invoicesModule\invoices\invoices.js
// renamed canBatchMarkAsPaid -> canBatchMarkOwnAsPaid
export const canBatchMarkOwnAsPaid = (
  selectedInvoices: Invoice[] | CorrectiveInvoice[],
  user: User | null,
  allowedReplacementsByCode: string[],
) => {
  if (!selectedInvoices.length) {
    return false;
  }
  return selectedInvoices.every(
    (selectedInvoice: Invoice) =>
      !!selectedInvoice &&
      !selectedInvoice.paid &&
      (hasPrivilege(PRIVILEGES.Invoices.SETTLE_OWN_INVOICE, user) ||
        (hasPrivilege(PRIVILEGES.Invoices.SETTLE_OWN_INVOICE_OWNER, user) &&
          isLoggedUserOrReplacementByCode(
            selectedInvoice?.owner?.code,
            user,
            allowedReplacementsByCode,
          ))),
  );
};

// copied from original repository - storm/nautiqus
// frontend\src\main\webapp\frontend\src\modules\invoicesModule\invoices\foreignInvoices.js
// renamed canBatchMarkAsPaid -> canBatchMarkForeignAsPaid
export const canBatchMarkForeignAsPaid = (
  selectedInvoices: Invoice[] | CorrectiveInvoice[],
  user: User | null,
) => {
  if (!selectedInvoices.length) {
    return false;
  }
  return selectedInvoices.every(
    (selectedInvoice: Invoice) =>
      !!selectedInvoice &&
      !selectedInvoice.paid &&
      hasPrivilege(PRIVILEGES.Invoices.SETTLE_FOREIGN_INVOICE, user),
  );
};

// copied from original repository - storm/nautiqus
// frontend\src\main\webapp\frontend\src\modules\invoicesModule\invoices\administrativeInvoices.js
// renamed canBatchMarkAsPaid -> canBatchMarkAdministrativeAsPaid
export const canBatchMarkAdministrativeAsPaid = (
  selectedInvoices: Invoice[] | CorrectiveInvoice[],
  user: User | null,
  allowedReplacements: number[],
) => {
  if (!selectedInvoices.length) {
    return false;
  }
  return selectedInvoices.every(
    (selectedInvoice: Invoice) =>
      !!selectedInvoice &&
      !selectedInvoice.paid &&
      (hasPrivilege(PRIVILEGES.Invoices.SETTLE_ADMIN_INVOICE, user) ||
        (hasPrivilege(PRIVILEGES.Invoices.SETTLE_ADMIN_INVOICE_OWNER, user) &&
          isLoggedUserOrReplacement(
            selectedInvoice?.owner?.id,
            user,
            allowedReplacements,
          ))),
  );
};

// copied from original repository - storm/nautiqus
export const canSendMail = (
  invoice: Invoice | CorrectiveInvoice,
  user: User | null,
  allowedReplacementsByCode: string[],
) => {
  if (invoice.deleted) {
    return false;
  }

  const isOwner = isLoggedUserOrReplacementByCode(
    invoice?.owner?.code,
    user,
    allowedReplacementsByCode,
  );

  if (isInvoiceAdministrative(invoice.invoiceClass)) {
    return (
      hasPrivilege(PRIVILEGES.Invoices.MAIL_ADMIN_INVOICE, user) ||
      (hasPrivilege(PRIVILEGES.Invoices.MAIL_ADMIN_INVOICE_OWNER, user) &&
        isOwner)
    );
  }

  if (isForeignInvoice(invoice.invoiceClass)) {
    return hasPrivilege(PRIVILEGES.Invoices.MAIL_FOREIGN_INVOICE, user);
  }

  return (
    hasPrivilege(PRIVILEGES.Invoices.MAIL_OWN_INVOICE, user) ||
    (hasPrivilege(PRIVILEGES.Invoices.MAIL_OWN_INVOICE_OWNER, user) && isOwner)
  );
};

// copied from original repository - storm/nautiqus
export const canReviewAllocation = (user: User | null) =>
  hasPrivilege(PRIVILEGES.Invoices.REVIEW_ALLOCATION, user);

// frontend\src\main\webapp\frontend\src\modules\invoicesModule\invoices\allocation.js
export const isAllocationOwner = (
  jobAccountEntry: AccountEntry,
  user: User | null,
  allowedReplacements: number[],
) =>
  isLoggedUserOrReplacement(
    jobAccountEntry.job?.owner?.id,
    user,
    allowedReplacements,
  );

export const canAddInvoice = (invoiceTab: InvoiceTab, user: User | null) =>
  ({
    [InvoiceTab.OWN]: hasPrivilege(
      PRIVILEGES.Invoices.CREATE_OWN_INVOICE,
      user,
    ),
    [InvoiceTab.FOREIGN]: hasPrivilege(
      PRIVILEGES.Invoices.ADD_FOREIGN_INVOICE,
      user,
    ),
    [InvoiceTab.ADMINISTRATIVE]: hasPrivilege(
      PRIVILEGES.Invoices.CREATE_ADMIN_INVOICE,
      user,
    ),
    [InvoiceTab.INTERNAL]: hasPrivilege(
      PRIVILEGES.Invoices.ADD_FOREIGN_INVOICE, //TODO: Eventually change to specific privilege for INTERNAL
      user,
    ),
  }[invoiceTab]);
